import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import { Link } from "react-router";
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { usePeriodeStore } from "@/hooks/use-periode";
import { getAnalytics70Iku } from "./api/analytics-70";

export default function ClusterIkuPage() {
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  const targetYear = semester === 2 ? year + 1 : year;
  const defaultMonth = semester === 2 ? "06" : "07";
  const [selectedMonth, setSelectedMonth] = useState(
    `${targetYear}_${defaultMonth}`,
  );

  const { data: rawData = [], isLoading } = useQuery({
    queryKey: ["analytics-70-iku", year, semester, selectedMonth],
    queryFn: () => getAnalytics70Iku(year, semester, selectedMonth),
    enabled: !!year && !!semester && !!selectedMonth,
  });

  const {
    scatterDataByFungsional,
    medianDataByFungsional,
    meanDataByFungsional,
    uniqueIkus,
    uniqueFungsional,
  } = useMemo(() => {
    const ikuSet = new Set<string>();
    const fungsionalSet = new Set<string>();

    rawData.forEach((d: any) => {
      fungsionalSet.add(d.fungsional || "Unknown");
      d.iku_list.forEach((i: any) => ikuSet.add(i.kode_iku));
    });

    const ikus = Array.from(ikuSet).sort();
    const fungsionals = Array.from(fungsionalSet).sort();

    const lData = ikus.map((iku, index) => {
      let maxFull = 0;
      rawData.forEach((d: any) => {
        const match = d.iku_list.find((x: any) => x.kode_iku === iku);
        if (match && match.max_point_full > maxFull) {
          maxFull = match.max_point_full;
        }
      });
      return {
        kode_iku: iku,
        max_point_full: maxFull,
        xValue: index,
        yValue: maxFull,
      };
    });

    const sDataByFungsional: Record<string, any[]> = {};
    fungsionals.forEach((f) => (sDataByFungsional[f] = []));

    rawData.forEach((d: any) => {
      const f = d.fungsional || "Unknown";
      d.iku_list.forEach((i: any) => {
        const ikuIndex = ikus.indexOf(i.kode_iku);
        const jitterX = (Math.random() - 0.5) * 0.75;
        const jitterY = (Math.random() - 0.5) * 1.5; // increase y jitter slightly
        sDataByFungsional[f].push({
          ...i,
          niu: d.niu,
          nama: d.nama,
          fungsional: f,
          xValue: ikuIndex + jitterX,
          yValue: i.real_point + jitterY,
          plotted_point: i.real_point,
        });
      });
    });

    const calcMedian = (arr: number[]) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const half = Math.floor(sorted.length / 2);
      if (sorted.length % 2 === 0)
        return (sorted[half - 1] + sorted[half]) / 2.0;
      return sorted[half];
    };

    const medianDataByFungsional: Record<string, any[]> = {};
    const meanDataByFungsional: Record<string, any[]> = {};

    fungsionals.forEach((f) => {
      medianDataByFungsional[f] = [];
      meanDataByFungsional[f] = [];
      ikus.forEach((iku, ikuIndex) => {
        const points = sDataByFungsional[f]
          .filter((p: any) => p.kode_iku === iku)
          .map((p: any) => p.plotted_point);

        if (points.length > 0) {
          medianDataByFungsional[f].push({
            xValue: ikuIndex,
            yValue: calcMedian(points),
            fungsional: f,
            isMedian: true,
          });
          meanDataByFungsional[f].push({
            xValue: ikuIndex,
            yValue: points.reduce((a, b) => a + b, 0) / points.length,
            fungsional: f,
            isMean: true,
          });
        }
      });
    });

    return {
      lineData: lData,
      scatterDataByFungsional: sDataByFungsional,
      medianDataByFungsional,
      meanDataByFungsional,
      uniqueIkus: ikus,
      uniqueFungsional: fungsionals,
    };
  }, [rawData]);

  const COLORS = [
    "#3b82f6",
    "#ef4444",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
  ];

  const monthNames = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-muted/30">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Distribusi Cluster IKU
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Sebaran data capaian IKU per Jabatan Fungsional
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-45 bg-white h-9 shadow-sm">
              <SelectValue placeholder="Pilih Bulan" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                const mYear = semester === 2 ? year + 1 : year;
                const val = `${mYear}_${m.toString().padStart(2, "0")}`;
                return (
                  <SelectItem key={val} value={val}>
                    {monthNames[m - 1]} {mYear}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Button variant="outline" asChild>
            <Link to="/admin/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kembali
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50/50 border-blue-100 shadow-sm relative overflow-hidden">
          <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none">
            <BarChart3 className="w-32 h-32 text-blue-600" />
          </div>
          <CardContent className="p-6 relative z-10">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-blue-600/80">
                Total Partisipan Dosen
              </span>
              <span className="text-3xl font-bold text-slate-800">
                {isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                ) : (
                  rawData.length
                )}
                {!isLoading && (
                  <span className="text-base font-normal text-slate-500 ml-2">
                    Dosen
                  </span>
                )}
              </span>
              <span className="text-sm font-medium text-slate-600 mt-1">
                telah memiliki pencapaian IKU pada periode ini
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl shadow-sm border-muted/40 overflow-hidden">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
          <CardTitle className="text-lg font-bold text-slate-800">
            Sebaran Nilai IKU (Point vs Max Point)
          </CardTitle>
          <CardDescription>
            Distribusi total poin capaian IKU masing-masing dosen di setiap IKU
            berdasarkan Jabatan Fungsional.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 relative">
          {isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs font-medium text-slate-600">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 border-t-2 border-slate-500 border-dashed"></div>
              <span>Nilai Median (sesuai warna jabatan)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 bg-slate-500"></div>
              <span>Nilai Rata-rata (sesuai warna jabatan)</span>
            </div>
          </div>
          <div className="h-187.5 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e2e8f0"
                />
                <XAxis
                  dataKey="xValue"
                  type="number"
                  name="IKU"
                  domain={[-0.5, uniqueIkus.length - 0.5]}
                  ticks={uniqueIkus.map((_, i) => i)}
                  tickFormatter={(val) => uniqueIkus[val as number] || ""}
                  tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                  tickMargin={10}
                  axisLine={{ stroke: "#cbd5e1" }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  dataKey="yValue"
                  type="number"
                  name="Point Capaian"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                />
                <ZAxis dataKey="max_point_full" range={[25, 25]} />
                <Tooltip
                  shared={false}
                  isAnimationActive={false}
                  cursor={{ strokeDasharray: "3 3", stroke: "#cbd5e1" }}
                  content={({ active, payload }: any) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      // Determine if we hovered over line or scatter
                      if (payload[0].dataKey === "max_point_full") {
                        return (
                          <div className="bg-white p-4 border border-slate-200 shadow-lg rounded-xl text-sm z-50">
                            <p className="font-bold text-slate-800">
                              {data.kode_iku}
                            </p>
                            <p className="text-slate-600 mt-1">
                              Max Point Full:{" "}
                              <span className="font-bold">
                                {data.max_point_full}
                              </span>
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="bg-white p-4 border border-slate-200 shadow-lg rounded-xl text-sm w-96 max-h-100 overflow-y-auto z-50">
                          <p className="font-bold text-slate-800 text-base mb-1">
                            {data.nama}
                          </p>
                          <p className="text-slate-500 mb-3 text-xs">
                            <span className="font-semibold text-slate-700">
                              {data.fungsional}
                            </span>{" "}
                            — {data.kode_iku} (NIU: {data.niu})
                          </p>

                          <div className="space-y-1">
                            <p className="text-xs text-slate-600">
                              <span className="font-medium text-slate-700">
                                Final Point:
                              </span>{" "}
                              {data.final_point}
                            </p>
                            <p className="text-xs text-slate-600">
                              <span className="font-medium text-slate-700">
                                Real Point:
                              </span>{" "}
                              {data.real_point}
                            </p>
                            <p className="text-xs text-slate-600">
                              <span className="font-medium text-slate-700">
                                Max Point:
                              </span>{" "}
                              {data.max_point}
                            </p>
                            <p className="text-xs text-slate-600">
                              <span className="font-medium text-slate-700">
                                Rupiah 12 Bulan:
                              </span>{" "}
                              Rp {data.rupiah_12_bulan?.toLocaleString("id-ID")}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: "20px" }}
                  iconType="circle"
                />

                {uniqueFungsional.map((fungsional: string, index: number) => {
                  const color = COLORS[index % COLORS.length];
                  return (
                    <React.Fragment key={fungsional}>
                      <Scatter
                        name={fungsional}
                        data={scatterDataByFungsional[fungsional]}
                        dataKey="yValue"
                        fill={color}
                        fillOpacity={0.6}
                        isAnimationActive={false}
                        shape="circle"
                      />
                      <Scatter
                        name={`Median ${fungsional}`}
                        data={medianDataByFungsional[fungsional]}
                        line={{
                          stroke: color,
                          strokeWidth: 2,
                          strokeDasharray: "4 4",
                        }}
                        shape={<circle r={0} />}
                        fill="none"
                        legendType="none"
                        isAnimationActive={false}
                      />
                      <Scatter
                        name={`Mean ${fungsional}`}
                        data={meanDataByFungsional[fungsional]}
                        line={{ stroke: color, strokeWidth: 2 }}
                        shape={<circle r={0} />}
                        fill="none"
                        legendType="none"
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  );
                })}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
